import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEye, faList, faUsers, faComments } from '@/fontawesome';
import { DAY_TRADING_ROUTES } from '@/constants/routes';
import { useDayTradingWebSocket } from '@/hooks/useDayTradingWebSocket';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import ConnectionStatusIndicator from '@/components/daytrading/components/ConnectionStatusIndicator';
import CardSizeSlider from '@/components/daytrading/components/CardSizeSlider';
import ModeratorTradeCard from '@/components/daytrading/components/ModeratorTradeCard.tsx';
import ParticipantsList from '@/components/daytrading/components/ParticipantsList.tsx';
import HistoryPanel from '@/components/daytrading/components/HistoryPanel';
import ChatPanel from '@/components/daytrading/components/ChatPanel';
import StreamPiP from '@/components/daytrading/components/StreamPiP';
import StreamHeaderButton from '@/components/daytrading/components/StreamHeaderButton';
import NumberInput from '@/components/forms/NumberInput';
import SoundMuteButton from '@/components/daytrading/components/SoundMuteButton';
import { getStreamSourceKey } from '@/utils/stream-info';
import { useSoundMuted } from '@/hooks/useSoundMuted';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import { useUser } from '@/contexts/UserContext';
import type { DayTradingSessionID, UserID } from '@/models/ids';
import type { DayTradingSessionChat } from '@/models/day-trading-session';
import { sortDayTrades } from '@/utils/daytrade-calculations';

export default function DayTradingObserverPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const dtsid = sessionId as DayTradingSessionID;

  const {
    initialLoading,
    initialError,
    connectionStatus,
    error,
    sessionDeleted,
    sessionState,
    sessionClosed,
    participants,
    moderators,
    moderatorPings,
    moderatorPolls,
    streamInfo,
    chat,
    setChat,
  } = useDayTradingWebSocket({ sessionId: dtsid, role: 'observer' });

  const { user, isEmployee, isAdmin } = useUser();
  const { isMuted, toggleMuted } = useSoundMuted();
  const { play } = useNotificationSound(isMuted);

  const currentUserId = (user?.id ?? '') as UserID;
  const chatLastViewedId = chat?.lastViewed[currentUserId]?.messageID ?? null;
  const chatLatest = chat !== null && chat.messages.length > 0 ? chat.messages[chat.messages.length - 1] : null;
  const chatHasUnread =
    chatLatest !== null && chatLatest.id !== chatLastViewedId && chatLatest.userID !== currentUserId;

  const handleChatUpdate = useCallback(
    (next: DayTradingSessionChat) => setChat(next),
    [setChat],
  );

  const handleOpenStandaloneChat = useCallback(() => {
    window.open(
      `${DAY_TRADING_ROUTES.CHAT(dtsid)}?role=observer`,
      '_blank',
      'width=500,height=800,resizable=yes,scrollbars=yes',
    );
    setChatMinimized(true);
  }, [dtsid]);

  const [cardScale, setCardScale] = useState(1.0);
  const [budgetInput, setBudgetInput] = useState('100000');
  const [historyMinimized, setHistoryMinimized] = useState(false);
  const [participantsMinimized, setParticipantsMinimized] = useState(false);
  const [livestreamMinimized, setLivestreamMinimized] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);

  // Auto-restore PiP when an active stream source appears or changes
  const activeStreamSource = getStreamSourceKey(streamInfo);
  const prevStreamSourceRef = useRef(activeStreamSource);
  useEffect(() => {
    if (activeStreamSource !== prevStreamSourceRef.current) {
      prevStreamSourceRef.current = activeStreamSource;
      if (activeStreamSource !== '') {
        setLivestreamMinimized(false);
      }
    }
  }, [activeStreamSource]);
  const budgetCents = Math.round(parseFloat(budgetInput || '0') * 100);
  const trades = sortDayTrades(sessionState?.trades ?? [], new Set());

  if (initialLoading) {
    return <LoadingSpinner message="Lade Session..." />;
  }

  if (initialError !== null) {
    return (
      <div className="text-center py-12">
        <p className="text-lg text-red-600 dark:text-red-400 mb-4">
          {initialError}
        </p>
        <Link
          to={DAY_TRADING_ROUTES.LIST}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  if (sessionDeleted) {
    return (
      <div className="text-center py-12">
        <p className="text-lg text-red-600 dark:text-red-400 mb-4">
          Diese Session wurde gelöscht.
        </p>
        <Link
          to={DAY_TRADING_ROUTES.LIST}
          className="text-blue-600 dark:text-blue-400 hover:underline"
        >
          Zurück zur Übersicht
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Back link */}
      <div className="mb-6">
        <Link
          to={DAY_TRADING_ROUTES.LIST}
          className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="w-4 h-4 mr-2" />
          Zurück zur Übersicht
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faEye} className="w-6 h-6 text-gray-500" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Daytrading Session: Beobachter
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <CardSizeSlider scale={cardScale} onScaleChange={setCardScale} />
          <SoundMuteButton isMuted={isMuted} onToggle={toggleMuted} play={play} />
          <ConnectionStatusIndicator status={connectionStatus} />
        </div>
      </div>

      {/* Error */}
      {error !== null && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Budget (local only, not persisted) */}
      <div className="mb-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm px-5 py-3 flex items-center gap-3">
        <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
          Depotgröße
        </label>
        <NumberInput
          value={budgetInput}
          onChange={setBudgetInput}
          placeholder="100000"
          suffix="$"
          ariaLabel="Depotgröße"
        />
        <div className="flex-1" />
        {chatMinimized && (
          <button
            onClick={() => setChatMinimized(false)}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border cursor-pointer transition-colors ${
              chatHasUnread
                ? 'border-blue-500 text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 animate-pulse'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
            title={chatHasUnread ? 'Neue Nachrichten' : 'Chat öffnen'}
          >
            <FontAwesomeIcon icon={faComments} className="w-4 h-4" />
            Chat
            {chatHasUnread && (
              <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />
            )}
          </button>
        )}
        <StreamHeaderButton
          streamInfo={streamInfo}
          sessionClosed={sessionClosed}
          variant="viewer"
          minimized={livestreamMinimized}
          onOpenPiP={() => setLivestreamMinimized(false)}
        />
        {participantsMinimized && (
          <button
            onClick={() => setParticipantsMinimized(false)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
          >
            <FontAwesomeIcon icon={faUsers} className="w-4 h-4" />
            Teilnehmer
          </button>
        )}
        {historyMinimized && (
          <button
            onClick={() => setHistoryMinimized(false)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
          >
            <FontAwesomeIcon icon={faList} className="w-4 h-4" />
            Historie
          </button>
        )}
      </div>

      {/* Session closed banner */}
      {sessionClosed && (
        <div className="mb-6 p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-800 dark:text-yellow-200">
          Diese Session wurde beendet.
        </div>
      )}

      {/* Content: trades + participants */}
      <div className={!historyMinimized || !participantsMinimized || !chatMinimized ? 'grid grid-cols-1 xl:grid-cols-[1fr_auto] gap-6' : ''}>
        {/* Trades grid */}
        {trades.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Noch keine Trades in dieser Session.
          </div>
        ) : (
          <div className="flex flex-wrap gap-5 self-start" style={{ zoom: cardScale }}>
            {trades.map((trade) => (
              <ModeratorTradeCard
                key={trade.id}
                trade={trade}
                sessionId={dtsid}
                disabled={true}
                onStateUpdated={() => {}}
                budget={budgetCents}
                participantState={null}
              />
            ))}
          </div>
        )}

        {/* Participants sidebar */}
        {(!historyMinimized || !participantsMinimized || !chatMinimized) && (
          <div className="min-w-80 flex flex-col gap-6">
            {!participantsMinimized && (
              <ParticipantsList
                participants={participants}
                moderators={moderators}
                moderatorPings={moderatorPings}
                moderatorPolls={moderatorPolls}
                sessionClosed={sessionClosed}
                onMinimize={() => setParticipantsMinimized(true)}
              />
            )}
            {!historyMinimized && (
              <HistoryPanel history={sessionState?.history ?? []} onMinimize={() => setHistoryMinimized(true)} />
            )}
            {!chatMinimized && chat !== null && user !== null && (
              <ChatPanel
                chat={chat}
                sessionId={dtsid}
                currentUserId={currentUserId}
                canWrite={false}
                canDelete={isEmployee || isAdmin}
                isMuted={isMuted}
                onChatUpdate={handleChatUpdate}
                onMinimize={() => setChatMinimized(true)}
                onOpenStandalone={handleOpenStandaloneChat}
              />
            )}
          </div>
        )}
      </div>

      <StreamPiP
        streamInfo={streamInfo}
        minimized={livestreamMinimized}
        onMinimize={() => setLivestreamMinimized(true)}
      />
    </div>
  );
}
